import dayjs from "dayjs";
import { BaseAction } from "../../../core/actions/BaseAction";
import { STORAGE_KEYS } from "../../../Lib/Constants/storageKeys";
import { isActionRunSuccessfully, shouldSharePlaceWithGuardian } from "../../../Lib/Utils/validationUtil";
import { updateRecoilState } from "../../../Orchestrator/Recoils/Events/Recoil";
import { IBookedOffer, IBookingResponse, IBookingTypeTrip, IQuickExchangeOfferRequest } from "../../../Orchestrator/Types/Index";
import { IDefaultActionResult } from "../../../Orchestrator/Types/Interface/ActionParams/DefaultActionResult";
import { IGetAllTripWithoutExchangeActionParams } from "../../../Orchestrator/Types/Interface/ActionParams/FilterTripAction";
import { fetchExchangeOffersAction } from "../../LogicActions/ChangeDate/FetchExchangeOffersAction";
import { getAllOfferWithoutExchangeAction } from "../../LogicActions/CommonActions/GetAllOfferWithoutExchangeAction";
import { getAllTripWithoutExchangeAction } from "../../LogicActions/CommonActions/GetAllTripWithoutExchangeAction";
import { TrainTypeEnum, TripTypeEnum } from "../../../Orchestrator/Types/Enums/globalDefaultEnum";

import { preferTrainTrips } from "../../../Lib/Utils/booking";

export class ChangeDateContinueClickAction extends BaseAction<IDefaultActionResult, {
    redirectPath: string,
    selectedRange: [dayjs.Dayjs | null, dayjs.Dayjs | null],
    booking: IBookingResponse,
    selectedPassengerForCancelInBound: string[],
    selectedPassengerForCancel: string[],
    selectedDate: string,
    inboundSelectedDate: string
}> {
    override async run(): Promise<IDefaultActionResult> {
        const { redirectPath, selectedRange, booking, selectedPassengerForCancelInBound, selectedPassengerForCancel, selectedDate, inboundSelectedDate } = this.params!;
        let result: IDefaultActionResult = {
            data: null,
            message: "",
            status: "fail"
        }
        updateRecoilState("trainAtom", null);
        updateRecoilState("selectedPassengerForCancelAtom", null);
        updateRecoilState("selectedPassengerForCancelInBoundAtom", null);
        updateRecoilState("selectedPassengerForCancelAtom", selectedPassengerForCancel);
        updateRecoilState("selectedPassengerForCancelInBoundAtom", selectedPassengerForCancelInBound);
        updateRecoilState("selectedOutboundTrainAtom", null);
        updateRecoilState("selectedInboundTrainAtom", null);
        updateRecoilState("tripLegsDetailsAtom", {
            outbound: {},
            inbound: {}
        });
        updateRecoilState("selectedTrainLegsAtom", {
            outbound: [],
            inbound: []
        });

        localStorage.removeItem(STORAGE_KEYS.CHANGE_DATE_FLOW);
        localStorage.removeItem("isChangeDateFlowForToast");
        localStorage.setItem(STORAGE_KEYS.CHANGE_DATE_FLOW, "true");
        localStorage.setItem("isChangeDateFlowForToast", "true");

        if (!selectedRange[0]) {
            updateRecoilState("trainTypeAtom", TrainTypeEnum.inbound);
            const anonymousPassengerSpecifications =
                booking?.booking?.passengers.filter(p =>
                    selectedPassengerForCancelInBound.includes(p.id)
                ).map((passenger) => ({
                    id: passenger.id,
                    type: passenger.type,
                    externalRef: passenger.externalRef,
                    age: passenger.age,
                    cards: passenger?.cards || [],
                    prmNeeds: passenger?.prmNeeds || [],
                    sharePlaceWithGuardian: shouldSharePlaceWithGuardian(passenger) ? true : undefined
                })) || [];

            await getAllOfferWithoutExchangeAction.setParams(booking?.booking?.bookedOffers)
            await getAllOfferWithoutExchangeAction.execute()
            let bookingOfferWithoutExchange: IBookedOffer[] = getAllOfferWithoutExchangeAction.result!


            const params: IGetAllTripWithoutExchangeActionParams = {
                bookedOffers: bookingOfferWithoutExchange,
                trips: booking?.booking?.trips
            }
            await getAllTripWithoutExchangeAction.setParams(params)
            await getAllTripWithoutExchangeAction.execute()
            const allTripWithoutExchange: IBookingTypeTrip[] = getAllTripWithoutExchangeAction.result!

            const inboundTrip = preferTrainTrips(allTripWithoutExchange).find(trip => trip.direction === TripTypeEnum.IN_BOUND);
            const matchingInboundTrip = inboundTrip
                ? booking?.booking?.trips.find(trip => trip.id === inboundTrip.id)
                : null;
            const fallbackInboundTrip = preferTrainTrips(booking?.booking.trips).find(t => t.direction === TripTypeEnum.IN_BOUND);

            const inboundBookedOfferOnly = bookingOfferWithoutExchange.filter(offer => inboundTrip?.id === offer?.tripCoverage?.coveredTripId)

            // ERM infant–adult linking: the backend now requires the WHOLE
            // linked group in the exchange request (400 PASSENGERS_MISSING_CHILDREN
            // otherwise), so lap-infant admissions are included with their adult.


            let admissions = inboundBookedOfferOnly?.flatMap(offer =>
                offer?.admissions?.filter((admission) =>
                    admission.status === "FULFILLED" &&
                    admission.passengerIds.some(pid =>
                        anonymousPassengerSpecifications.some(p => p.id === pid)
                    )
                ) || []
            );

            const fulfillmentIds: string[] = [];

            admissions?.forEach(element => {
                fulfillmentIds.push(element.id);
            });

            // Calculate original price of items being exchanged
            let originalPriceOfExchangedItemsInbound = 0;
            if (booking?.booking?.bookedOffers && fulfillmentIds.length > 0) {
                booking.booking.bookedOffers.forEach(offer => {
                    offer.admissions?.forEach(admission => {
                        if (fulfillmentIds.includes(admission.id)) {
                            originalPriceOfExchangedItemsInbound += admission.price.amount;
                        }
                    });
                });
            }

            if (originalPriceOfExchangedItemsInbound > 0) {
                const scale = booking.booking.bookedOffers[0]?.admissions[0]?.price?.scale;
                updateRecoilState("originalPriceOfExchangedItemsInboundAtom", null);
                updateRecoilState("originalPriceOfExchangedItemsInboundAtom", (originalPriceOfExchangedItemsInbound / ((100 * scale) / scale)));
            }
            const exchangeOffersRequest: IQuickExchangeOfferRequest = {
                fulfillmentIds: fulfillmentIds,
                overruleCode: "",
                tripSearchCriteria: {
                    departureTime: selectedRange[1]?.format("YYYY-MM-DD") + "T00:00:00",
                    origin: {
                        objectType: "StopPlaceRef",
                        stopPlaceRef: matchingInboundTrip?.origin.stopPlaceRef || fallbackInboundTrip?.origin.stopPlaceRef || "",
                    },
                    destination: {
                        objectType: "StopPlaceRef",
                        stopPlaceRef: matchingInboundTrip?.destination.stopPlaceRef || fallbackInboundTrip?.destination.stopPlaceRef || "",
                    },
                    direction: TripTypeEnum.IN_BOUND,
                },
                offerSearchCriteria: {
                    currency: "AED",
                },
                // Include the full linked group; the sharePlaceWithGuardian flag
                // tells the backend which specs are lap infants.
                anonymousPassengerSpecifications: anonymousPassengerSpecifications
                    .map(passenger => {
                        return {
                            externalRef: passenger.externalRef,
                            type: passenger.type,
                            cards: passenger?.cards || [],
                            prmNeeds: passenger.prmNeeds || [],
                            age: passenger?.age,
                            sharePlaceWithGuardian: passenger.sharePlaceWithGuardian || undefined,
                        }
                    }),
            };
            try {
                await fetchExchangeOffersAction.setParams(
                    {
                        booking_id: booking?.booking.id,
                        exchangeOffersRequest: exchangeOffersRequest,
                        booking: booking,
                        originalPriceOfExchangedItems: originalPriceOfExchangedItemsInbound,
                        trainType: TrainTypeEnum.inbound,
                        selectedDate: selectedDate,
                        inboundSelectedDate: inboundSelectedDate
                    }
                )
                await fetchExchangeOffersAction.execute()
                if (isActionRunSuccessfully(fetchExchangeOffersAction)) {
                    const fetchResult: IDefaultActionResult = fetchExchangeOffersAction.result!
                    if (fetchResult.status == "success") {
                        result.status = "success"
                    }
                }

            } catch (error) {
                console.error("Error fetching exchange offers:", error);
                result.status = "fail"
            }
            finally {
                return result;
            }
        } else {
            updateRecoilState("trainTypeAtom", TrainTypeEnum.outbound);

            const anonymousPassengerSpecifications =
                booking?.booking?.passengers.filter(p =>
                    selectedPassengerForCancel.includes(p.id)
                ).map((passenger) => ({
                    id: passenger.id,
                    type: passenger.type,
                    externalRef: passenger.externalRef,
                    age: passenger.age,
                    cards: passenger.cards || [],
                    prmNeeds: passenger?.prmNeeds || [],
                    sharePlaceWithGuardian: shouldSharePlaceWithGuardian(passenger) ? true : undefined
                })) || [];


            await getAllOfferWithoutExchangeAction.setParams(booking?.booking?.bookedOffers)
            await getAllOfferWithoutExchangeAction.execute()
            let bookingOfferWithoutExchange: IBookedOffer[] = getAllOfferWithoutExchangeAction.result!

            const params: IGetAllTripWithoutExchangeActionParams = {
                bookedOffers: bookingOfferWithoutExchange,
                trips: booking?.booking?.trips
            }
            await getAllTripWithoutExchangeAction.setParams(params)
            await getAllTripWithoutExchangeAction.execute()
            const allTripWithoutExchange: IBookingTypeTrip[] = getAllTripWithoutExchangeAction.result!

            const outboundTrip = preferTrainTrips(allTripWithoutExchange).find(trip => trip.direction === TripTypeEnum.OUT_BOUND);
            const matchingOutboundTrip = outboundTrip
                ? booking?.booking?.trips?.find(trip => trip.id === outboundTrip.id)
                : null;
            const fallbackOutboundTrip = preferTrainTrips(booking?.booking?.trips).find(t => t.direction === TripTypeEnum.OUT_BOUND);

            const outboundBookedOfferOnly = bookingOfferWithoutExchange?.filter(offer => outboundTrip?.id === offer?.tripCoverage?.coveredTripId)

            // ERM infant–adult linking: the backend now requires the WHOLE
            // linked group in the exchange request (400 PASSENGERS_MISSING_CHILDREN
            // otherwise), so lap-infant admissions are included with their adult.


            let admissions = outboundBookedOfferOnly?.flatMap(offer =>
                offer?.admissions?.filter((admission) =>
                    admission.status === "FULFILLED" &&
                    admission.passengerIds.some(pid =>
                        anonymousPassengerSpecifications.some(p => p.id === pid)
                    )
                ) || []
            );

            const fulfillmentIds: string[] = [];

            admissions?.forEach(element => {
                fulfillmentIds.push(element.id);
            });
            // Calculate original price of items being exchanged
            let originalPriceOfExchangedItemsOutbound = 0;
            if (booking?.booking?.bookedOffers && fulfillmentIds.length > 0) {
                booking.booking.bookedOffers.forEach(offer => {
                    offer.admissions?.forEach(admission => {
                        if (fulfillmentIds.includes(admission.id)) {
                            originalPriceOfExchangedItemsOutbound += admission.price.amount;
                        }
                    });
                });
            }

            if (originalPriceOfExchangedItemsOutbound > 0) {
                const scale = booking.booking.bookedOffers[0]?.admissions[0]?.price?.scale;
                updateRecoilState("originalPriceOfExchangedItemsOutboundAtom", null);
                updateRecoilState("originalPriceOfExchangedItemsOutboundAtom", (originalPriceOfExchangedItemsOutbound / ((100 * scale) / scale)));
            }
            // Get passenger specifications from the booking

            const exchangeOffersRequest: IQuickExchangeOfferRequest = {
                fulfillmentIds: fulfillmentIds,
                overruleCode: "",
                tripSearchCriteria: {
                    departureTime: selectedRange[0]?.format("YYYY-MM-DD") + "T00:00:00",
                    origin: {
                        objectType: "StopPlaceRef",
                        stopPlaceRef: matchingOutboundTrip?.origin.stopPlaceRef || fallbackOutboundTrip?.origin.stopPlaceRef || "",
                    },
                    destination: {
                        objectType: "StopPlaceRef",
                        stopPlaceRef: matchingOutboundTrip?.destination.stopPlaceRef || fallbackOutboundTrip?.destination.stopPlaceRef || "",
                    },
                    direction: TripTypeEnum.OUT_BOUND,
                },
                offerSearchCriteria: {
                    currency: "AED",
                },
                // Include the full linked group; the sharePlaceWithGuardian flag
                // tells the backend which specs are lap infants.
                anonymousPassengerSpecifications: anonymousPassengerSpecifications
                    .map(passenger => {
                        return {
                            externalRef: passenger.externalRef,
                            type: passenger.type,
                            cards: passenger?.cards || [],
                            prmNeeds: passenger.prmNeeds || [],
                            age: passenger?.age,
                            sharePlaceWithGuardian: passenger.sharePlaceWithGuardian || undefined,
                        }
                    }),
            };

            try {
                await fetchExchangeOffersAction.setParams(
                    {
                        booking_id: booking?.booking.id,
                        exchangeOffersRequest: exchangeOffersRequest,
                        booking: booking,
                        originalPriceOfExchangedItems: originalPriceOfExchangedItemsOutbound,
                        trainType: TrainTypeEnum.outbound,
                        selectedDate: selectedDate,
                        inboundSelectedDate: inboundSelectedDate
                    }
                )
                await fetchExchangeOffersAction.execute()
                if (isActionRunSuccessfully(fetchExchangeOffersAction)) {
                    const fetchResult: IDefaultActionResult = fetchExchangeOffersAction.result!
                    if (fetchResult.status == "success") {
                        result.status = "success"
                    }
                }
            } catch (error) {
                console.error("Error fetching exchange offers:", error);
                result.status = "fail"
            }
            finally {
                return result;
            }
        }
    }
}

export const changeDateContinueClickAction = new ChangeDateContinueClickAction();
